import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import { ErrorBox } from '../../components/Loader.jsx';
import SortableTh from '../../components/SortableTh.jsx';
import { RoutineTasksAPI } from '../../lib/api';
import { useTableSort } from '../../lib/tableSort.js';
import { buildPath } from '../../config';

/**
 * Routine-Task Excel import page.
 *
 * The page is split into two columns:
 *   * left  — instructions: column-by-column reference, examples and
 *             "Tips" for the people who'll actually fill the file in.
 *   * right — the upload form and (after a submit) a per-row report
 *             produced by ``RoutineTaskViewSet.import_xlsx``.
 *
 * The upload talks to ``POST /api/task/routine-tasks/import/`` via
 * ``RoutineTasksAPI.importXlsx``.  When at least one row is created
 * the user is offered a one-click jump back to the list page; a few
 * inevitable per-row errors don't block the rest of the import — the
 * server applies the same "skip + report" policy as the legacy
 * server-rendered ``routine_task_import`` view.
 */

const COLUMN_REFERENCE = [
  {
    name: 'title',
    required: 'Required',
    desc: 'Free text, up to 300 characters.',
  },
  {
    name: 'description',
    required: 'Optional',
    desc: 'Free text.',
  },
  {
    name: 'start_date',
    required: 'Optional',
    desc: 'YYYY-MM-DD (e.g. 2025-01-15). Defaults to today.',
  },
  {
    name: 'end_date',
    required: 'Optional',
    desc: 'YYYY-MM-DD. Must be on/after start_date.',
  },
  {
    name: 'estimated_hours',
    required: 'Optional',
    desc: 'Whole number.',
  },
  {
    name: 'task_frequency',
    required: 'Required',
    desc: 'One of daily, weekly, monthly, annually.',
  },
  {
    name: 'cron_weekly_day',
    required: 'Required when task_frequency = weekly',
    desc: 'Monday, Tuesday, Wednesday, Thursday, Friday, Saturday or Sunday.',
  },
  {
    name: 'cron_monthly_date',
    required: 'Required when task_frequency = monthly',
    desc: 'Whole number between 1 and 31.',
  },
  {
    name: 'cron_annually_date',
    required: 'Required when task_frequency = annually',
    desc: 'Whole number between 1 and 31.',
  },
  {
    name: 'cron_annually_month',
    required: 'Required when task_frequency = annually',
    desc: 'Number 1–12 or month name (January … December).',
  },
  {
    name: 'team',
    required: 'Optional',
    desc: 'individual (default) or group.',
  },
  {
    name: 'status',
    required: 'Optional',
    desc: 'not_started (default), in_progress or completed.',
  },
  {
    name: 'approval_required',
    required: 'Optional',
    desc: 'True/False, yes/no or 1/0.',
  },
  {
    name: 'users',
    required: 'Optional',
    desc:
      'Comma- or semicolon-separated list of email addresses. Each email '
      + 'must already exist in your organisation; unknown emails are '
      + 'skipped (and listed in the report).',
  },
];

export default function RoutineTaskImport() {
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const [picked, setPicked] = useState(null);     // currently chosen File
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);      // server response payload
  const [error, setError] = useState(null);
  const { sortedRows: columnRefRows, sort: columnRefSort, toggleSort: toggleColumnRefSort } = useTableSort(
    COLUMN_REFERENCE,
    {
      getValue: (row, key) => {
        if (key === 'name') return row.name;
        if (key === 'required') return row.required;
        if (key === 'desc') return row.desc;
        return row[key];
      },
    },
  );

  // The ``downloadTemplate`` button calls the API directly to grab the
  // header-only workbook so the user has a guaranteed-correct
  // skeleton.  We use the shared blob-download helper inline here so
  // this page stays self-contained.
  const onDownloadTemplate = () => {
    setError(null);
    RoutineTasksAPI.downloadTemplate()
      .then((response) => {
        const blob = new Blob([response.data], {
          type:
            response.headers['content-type'] ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'routine_tasks_template.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => setError(e));
  };

  const onPick = (e) => {
    const file = e.target.files && e.target.files[0];
    setPicked(file || null);
    setReport(null);
    setError(null);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!picked) {
      setError(new Error('Please choose an .xlsx workbook to upload.'));
      return;
    }
    if (!picked.name.toLowerCase().endsWith('.xlsx')) {
      setError(new Error('Only .xlsx workbooks are supported. Please save your file as .xlsx and try again.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    setReport(null);
    RoutineTasksAPI.importXlsx(picked)
      .then((data) => {
        setReport(data);
        // Reset the file input so the user can immediately re-upload
        // a corrected version without an extra click.
        if (fileRef.current) fileRef.current.value = '';
        setPicked(null);
      })
      .catch((e) => {
        // The server may return 400 with a structured body (no rows
        // could be imported) — surface its details so the user knows
        // what to fix.  ``e.response.data`` is the structured payload.
        if (e && e.response && e.response.data) {
          setReport(e.response.data);
        } else {
          setError(e);
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <PageHeader
        title="Import Routine Tasks from Excel"
        subtitle="Bulk-create routine tasks by uploading an .xlsx workbook."
        actions={
          <Link className="ty-btn ty-btn--ghost" to={buildPath.routineTasks()}>
            ← Back to list
          </Link>
        }
      />

      <ErrorBox error={error} />

      <div
        className="ty-import-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {/* ---------- Instructions ---------- */}
        <section className="ty-card" style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>How to import</h3>
          <p>
            Use this page to bulk-create routine tasks from an Excel
            workbook. The importer reads the <strong>first sheet</strong> of
            an <code>.xlsx</code> file. Each data row becomes one routine
            task, owned by the current user and the current organisation.
          </p>

          <ol>
            <li>
              <button
                type="button"
                className="ty-btn ty-btn--ghost ty-btn--sm"
                onClick={onDownloadTemplate}
              >
                Download blank template
              </button>{' '}
              — has the correct headers and a sample row.
            </li>
            <li>Or, export your existing routine tasks from the list page to use as a starting point.</li>
            <li>Fill in one row per routine task. Keep the header row exactly as it is.</li>
            <li>Save the workbook as <code>.xlsx</code> (Excel Workbook).</li>
            <li>Pick the file in the panel on the right and click <strong>Upload &amp; import</strong>.</li>
          </ol>

          <h4>Column reference</h4>
          <div style={{ overflowX: 'auto' }}>
            <table className="ty-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <SortableTh
                    label="Column"
                    sortKey="name"
                    activeKey={columnRefSort.key}
                    direction={columnRefSort.dir}
                    onSort={toggleColumnRefSort}
                    style={{ width: '24%' }}
                  />
                  <SortableTh
                    label="Required?"
                    sortKey="required"
                    activeKey={columnRefSort.key}
                    direction={columnRefSort.dir}
                    onSort={toggleColumnRefSort}
                    style={{ width: '28%' }}
                  />
                  <SortableTh
                    label="Format / accepted values"
                    sortKey="desc"
                    activeKey={columnRefSort.key}
                    direction={columnRefSort.dir}
                    onSort={toggleColumnRefSort}
                  />
                </tr>
              </thead>
              <tbody>
                {columnRefRows.map((col) => (
                  <tr key={col.name}>
                    <td><code>{col.name}</code></td>
                    <td>
                      {col.required === 'Required' ? (
                        <strong style={{ color: '#c0392b' }}>Required</strong>
                      ) : col.required.startsWith('Required when') ? (
                        <em>{col.required}</em>
                      ) : (
                        col.required
                      )}
                    </td>
                    <td>{col.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            className="ty-alert ty-alert--info"
            role="note"
            style={{ marginTop: 12 }}
          >
            <strong>Tips</strong>
            <ul style={{ marginBottom: 0 }}>
              <li>Column ordering does not matter as long as the header row is intact.</li>
              <li>Empty rows are skipped silently.</li>
              <li>The importer never updates existing routine tasks — every valid row is added as a new entry.</li>
              <li>If a row fails validation it is skipped and reported on the right; the rest of the file still imports.</li>
            </ul>
          </div>
        </section>

        {/* ---------- Upload form ---------- */}
        <section className="ty-card" style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Upload your file</h3>
          <form onSubmit={onSubmit}>
            <label className="ty-label" htmlFor="rt-import-file">
              Excel workbook (.xlsx)
            </label>
            <input
              ref={fileRef}
              id="rt-import-file"
              type="file"
              className="ty-input"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onPick}
              required
            />
            <small style={{ display: 'block', marginTop: 4, color: '#6b7280' }}>
              Only one file per upload. Older <code>.xls</code> workbooks are
              not supported — please save as <code>.xlsx</code>.
            </small>

            <div style={{ marginTop: 12 }}>
              <button
                type="submit"
                className="ty-btn"
                disabled={submitting || !picked}
              >
                {submitting ? 'Uploading…' : '⬆ Upload & import'}
              </button>{' '}
              <Link className="ty-btn ty-btn--ghost" to={buildPath.routineTasks()}>
                Cancel
              </Link>
            </div>
          </form>

          {report && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ marginBottom: 6 }}>Import report</h4>

              {/* When the server returned a structured 400 it sets
                  ``detail`` instead of ``created/errors``.  Show that
                  prominently so the user knows the file was rejected
                  outright. */}
              {report.detail && (
                <div className="ty-alert ty-alert--error" role="alert">
                  {report.detail}
                </div>
              )}

              {typeof report.created === 'number' && (
                <ul className="ty-list" style={{ margin: 0 }}>
                  <li>
                    Routine tasks created:{' '}
                    <strong style={{ color: '#0a8754' }}>{report.created}</strong>
                  </li>
                  <li>
                    Rows skipped / warnings:{' '}
                    <strong style={{ color: '#b45309' }}>{report.errors || 0}</strong>
                  </li>
                </ul>
              )}

              {Array.isArray(report.error_details) && report.error_details.length > 0 && (
                <details open style={{ marginTop: 8 }}>
                  <summary>Details ({report.error_details.length})</summary>
                  <ul
                    style={{
                      maxHeight: 220,
                      overflowY: 'auto',
                      marginTop: 6,
                      paddingLeft: 18,
                    }}
                  >
                    {report.error_details.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </details>
              )}

              {report.created > 0 && (
                <div style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    className="ty-btn"
                    onClick={() => navigate(buildPath.routineTasks())}
                  >
                    View imported routine tasks →
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
